import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, Crown, CreditCard, Shield } from "lucide-react";

export default function ProRegistrationSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const [userData, setUserData] = useState(null);

  const sessionId = searchParams.get("session_id");
  const pendingId = searchParams.get("pending_id");
  const cancelled = searchParams.get("cancelled");

  useEffect(() => {
    if (cancelled) {
      setStatus("cancelled");
      return;
    }

    if (!sessionId || !pendingId) {
      setStatus("error");
      setErrorMessage("Missing registration information. Please try registering again.");
      return;
    }

    completeRegistration();
  }, [sessionId, pendingId, cancelled]);

  const completeRegistration = async () => {
    try {
      setStatus("verifying");
      
      const response = await axios.post(`${API}/auth/register/professional/complete`, {
        session_id: sessionId,
        pending_id: pendingId
      });

      if (response.data.success) {
        // Store the token
        localStorage.setItem("token", response.data.access_token);
        setUserData(response.data.user);
        setStatus("success");
        
        // Track conversion
        if (window.trackConversion) {
          window.trackConversion('professional_signup', 0);
        }
        
        toast.success("Account activated successfully!");
      } else {
        setStatus("error");
        setErrorMessage(response.data.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration completion error:", error);
      setStatus("error");
      setErrorMessage(error.response?.data?.detail || "Failed to complete registration. Please contact support.");
    }
  };

  const handleContinue = () => {
    // Reload the page to trigger auth context refresh, then navigate
    window.location.href = "/pricing";
  };

  const handleRetry = () => {
    navigate("/register/professional");
  };

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <div className="flex items-center justify-center min-h-screen pt-20 px-4">
        <div className="w-full max-w-lg animate-fadeInUp">
          {status === "verifying" && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 text-center">
                <Loader2 className="w-16 h-16 text-sienna mx-auto mb-4 animate-spin" />
                <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">
                  Completing Your Registration
                </h2>
                <p className="text-gray-600">
                  Setting up your account...
                </p>
              </CardContent>
            </Card>
          )}

          {status === "success" && (
            <Card className="border-0 shadow-xl" data-testid="registration-success">
              <CardContent className="p-8">
                {/* Success Header */}
                <div className="text-center mb-8">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">
                    Welcome to BQL, {userData?.full_name?.split(" ")[0]}!
                  </h2>
                  <p className="text-gray-600">
                    Your professional account is now active.
                  </p>
                </div>

                {/* Card Saved Confirmation */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">Card Saved Successfully</p>
                      <p className="text-sm text-green-700">Your card is on file for future billing</p>
                    </div>
                  </div>
                </div>

                {/* Next Steps */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">What's Next?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-sienna/20 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-sienna font-semibold text-sm">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Wait for Your First Lead</p>
                        <p className="text-sm text-gray-600">We'll notify you when a customer needs your services</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-sienna/20 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-sienna font-semibold text-sm">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Verify Your Identity</p>
                        <p className="text-sm text-gray-600">Get verified to build trust with customers</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-sienna/20 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-sienna font-semibold text-sm">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Start Winning Jobs</p>
                        <p className="text-sm text-gray-600">Respond to leads and grow your business</p>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Pricing Reminder */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-800">Simple Pricing</p>
                      <p className="text-sm text-blue-700">
                        Your first lead is FREE! After that, your $29.99/month subscription starts. Cancel anytime.
                      </p>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleContinue}
                  className="w-full h-12 bg-sienna hover:bg-sienna-dark rounded-full text-base"
                  data-testid="continue-to-pricing-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Continue to Subscription Plans
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}

          {status === "error" && (
            <Card className="border-0 shadow-xl" data-testid="registration-error">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">
                  Registration Failed
                </h2>
                <p className="text-gray-600 mb-6">
                  {errorMessage}
                </p>
                <Button 
                  onClick={handleRetry}
                  className="w-full h-12 bg-sienna hover:bg-sienna-dark rounded-full text-base"
                >
                  Try Again
                </Button>
              </CardContent>
            </Card>
          )}

          {status === "cancelled" && (
            <Card className="border-0 shadow-xl" data-testid="registration-cancelled">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-yellow-600" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">
                  Card Setup Cancelled
                </h2>
                <p className="text-gray-600 mb-6">
                  Your registration was not completed. We need your card on file for billing after your first lead (which is FREE!).
                </p>
                <Button 
                  onClick={handleRetry}
                  className="w-full h-12 bg-sienna hover:bg-sienna-dark rounded-full text-base"
                >
                  Complete Registration
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
